import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  TreeSelect,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/api/client';
import { useApiMutation, useDictionaries } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { CATEGORY_LABEL } from '@/lib/labels';

import type { TransportRequestRow } from './types';

interface Props {
  open: boolean;
  /** null — новая заявка */
  editing: TransportRequestRow | null;
  onClose: () => void;
}

interface FormValues {
  neededFrom: dayjs.Dayjs;
  neededTo?: dayjs.Dayjs | null;
  departmentId?: number | null;
  requesterName: string;
  requesterPhone?: string;
  vehicleCategory?: string | null;
  passengers?: number | null;
  cargoTons?: number | null;
  fromPoint?: string;
  toPoint?: string;
  flightNumber?: string;
  aircraftReg?: string;
  standNumber?: string;
  purpose?: string;
}

/**
 * Приём заявки диспетчером: кто заказал, когда и куда нужна машина.
 *
 * Маршрут — в тех же полях и той же раскладке, что задание путевого листа
 * (Office.taskLayout): при назначении он переносится в задание как есть.
 */
export function TransportRequestFormModal({ open, editing, onClose }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dictionaries = useDictionaries();
  const [form] = Form.useForm<FormValues>();

  const taskLayout = user?.activeOffice.taskLayout ?? 'FLIGHT';
  const hasLocationList = user?.activeOffice.taskAddressALocations ?? false;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (editing) {
      form.setFieldsValue({
        neededFrom: dayjs(editing.neededFrom),
        neededTo: editing.neededTo ? dayjs(editing.neededTo) : null,
        departmentId: editing.departmentId,
        requesterName: editing.requesterName,
        requesterPhone: editing.requesterPhone ?? undefined,
        vehicleCategory: editing.vehicleCategory,
        passengers: editing.passengers,
        cargoTons: editing.cargoTons !== null ? Number(editing.cargoTons) : null,
        fromPoint: editing.fromPoint ?? undefined,
        toPoint: editing.toPoint ?? undefined,
        flightNumber: editing.flightNumber ?? undefined,
        aircraftReg: editing.aircraftReg ?? undefined,
        standNumber: editing.standNumber ?? undefined,
        purpose: editing.purpose ?? undefined,
      });
    } else {
      form.setFieldsValue({ neededFrom: dayjs().add(1, 'hour').startOf('hour') });
    }
  }, [open, editing, form]);

  const save = useApiMutation(
    async (values: FormValues) => {
      const body = {
        ...values,
        neededFrom: values.neededFrom.toISOString(),
        neededTo: values.neededTo ? values.neededTo.toISOString() : null,
        departmentId: values.departmentId ?? null,
        vehicleCategory: values.vehicleCategory ?? null,
        passengers: values.passengers ?? null,
        cargoTons: values.cargoTons ?? null,
      };
      return editing
        ? (await api.patch(`/transport-requests/${editing.id}`, body)).data
        : (await api.post('/transport-requests', body)).data;
    },
    {
      successMessage: editing ? t('Заявка сохранена') : t('Заявка принята'),
      invalidate: [['transport-requests']],
    },
  );

  // Подразделения — деревом, как в остальных формах: заказчиком бывает и
  // отдел целиком, и его участок.
  const departments = dictionaries.data?.departments ?? [];
  interface DepartmentNode {
    value: number;
    title: string;
    children: DepartmentNode[];
  }
  const departmentTree = (parentId: number | null): DepartmentNode[] =>
    departments
      .filter((d) => d.parentId === parentId)
      .map((d) => ({ value: d.id, title: d.name, children: departmentTree(d.id) }));

  const regionName = Form.useWatch('fromPoint', form);
  const region = dictionaries.data?.regions.find((r) => r.name === regionName);

  return (
    <Modal
      open={open}
      title={editing ? `${t('Заявка')} ${editing.number}` : t('Новая заявка на транспорт')}
      okText={t('Сохранить')}
      cancelText={t('Отмена')}
      width={760}
      confirmLoading={save.isPending}
      onCancel={onClose}
      onOk={() =>
        void form.validateFields().then((values) => save.mutate(values, { onSuccess: onClose }))
      }
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="neededFrom"
              label={t('Когда нужна машина')}
              rules={[{ required: true, message: t('Укажите время подачи') }]}
            >
              <DatePicker showTime={{ format: 'HH:mm' }} format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="neededTo"
              label={t('До какого времени')}
              dependencies={['neededFrom']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value?: dayjs.Dayjs | null) =>
                    !value || value.isAfter(getFieldValue('neededFrom'))
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('Окончание должно быть позже начала'))),
                }),
              ]}
            >
              <DatePicker showTime={{ format: 'HH:mm' }} format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="departmentId" label={t('Подразделение-заказчик')}>
              <TreeSelect
                allowClear
                showSearch
                treeNodeFilterProp="title"
                treeDefaultExpandAll
                placeholder={t('Не из подразделений офиса')}
                treeData={departmentTree(null)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="requesterName"
              label={t('Кто заказал')}
              rules={[{ required: true, min: 2, message: t('Укажите заказчика') }]}
            >
              <Input maxLength={200} placeholder={t('ФИО или должность')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="requesterPhone" label={t('Телефон для связи')}>
              <Input maxLength={40} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="vehicleCategory" label={t('Какая техника нужна')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('Любая')}
                options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({
                  value,
                  label: t(label),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="passengers" label={t('Пассажиров')}>
              <InputNumber min={0} max={1000} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="cargoTons" label={t('Груз, т')}>
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        {taskLayout === 'REGION_DISTRICT' ? (
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="fromPoint" label={t('Регион')}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  onChange={() => form.setFieldValue('aircraftReg', undefined)}
                  options={dictionaries.data?.regions.map((r) => ({ value: r.name, label: r.name }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="aircraftReg" label={t('Район')}>
                <Select
                  allowClear
                  showSearch
                  disabled={!region}
                  optionFilterProp="label"
                  options={region?.districts.map((d) => ({ value: d.name, label: d.name }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="toPoint" label={t('Адрес Б')}>
                <Input maxLength={240} />
              </Form.Item>
            </Col>
          </Row>
        ) : taskLayout === 'ADDRESS' ? (
          <Row gutter={12}>
            <Col span={hasLocationList ? 8 : 12}>
              <Form.Item name="fromPoint" label={t('Адрес А')}>
                <Input maxLength={240} />
              </Form.Item>
            </Col>
            {hasLocationList && (
              <Col span={8}>
                <Form.Item name="aircraftReg" label={t('Локация')}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={dictionaries.data?.taskLocations.map((loc) => ({
                      value: loc.name,
                      label: loc.name,
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={hasLocationList ? 8 : 12}>
              <Form.Item name="toPoint" label={t('Адрес Б')}>
                <Input maxLength={240} />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="flightNumber" label={t('Рейс')}>
                <Input maxLength={16} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="aircraftReg" label={t('Борт')}>
                <Input maxLength={160} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="standNumber" label={t('Стоянка')}>
                <Input maxLength={16} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="toPoint" label={t('Куда')}>
                <Input maxLength={240} />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Form.Item name="purpose" label={t('Цель поездки')}>
          <Input.TextArea maxLength={400} autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
